import { useLingui } from '@lingui/react/macro';
import { CheckoutTypeParam } from 'src/api/endpoints/storeApi.types';
import { Feedback, SomethingWentWrong, Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { URLS } from 'src/utils/urls/urls';

import { useMemo } from 'react';
import { ClientType } from 'src/api/endpoints/cloudApi.types';
import { useDevices } from 'src/api/queries';
import { SubscriptionSummaryCard } from '../components/SubscriptionSummaryCard';
import { useCheckoutConfirmation } from '../useCheckoutConfirmation';

export const ConnectOfferConfirmationPage = () => {
  const { t } = useLingui();
  const { isPending, isError, error, paymentConfirm, subscription } =
    useCheckoutConfirmation({
      checkoutType: CheckoutTypeParam.CONNECT_OFFER,
    });

  const devices = useDevices();

  const commentContext = useMemo(() => {
    if (devices.isPending || devices.isError) {
      return {
        devices: 'Unable to fetch devices',
      };
    }
    const rm1List = devices.data.find((d) => d.clientType === ClientType.RM10)
      ? 'reMarkable 1'
      : false;
    const rm2List = devices.data.find((d) => d.clientType === ClientType.RM11)
      ? 'reMarkable 2'
      : false;
    const rmPaperProList = devices.data.find(
      (d) => d.clientType === ClientType.RM02
    )
      ? 'reMarkable Paper Pro'
      : false;
    const rmPaperProMoveList = devices.data.find(
      (d) => d.clientType === ClientType.RM03
    )
      ? 'reMarkable Paper Pro Move'
      : false;
    const rmPaperPureList = devices.data.find(
      (d) => d.clientType === ClientType.RM12
    )
      ? 'reMarkable Paper Pure'
      : false;

    const deviceList = [
      rm1List,
      rm2List,
      rmPaperProList,
      rmPaperProMoveList,
      rmPaperPureList,
    ]
      .filter(Boolean)
      .join(', ');

    const context = {
      devices: deviceList || 'No devices',
    };

    return context;
  }, [devices.data]);

  if (isPending) {
    return <Spinner />;
  }
  if (isError) {
    return <SomethingWentWrong error={error} />;
  }

  if (!paymentConfirm) {
    return (
      <SomethingWentWrong
        error={
          new Error(
            'Tried to render ConnectOfferConfirmationPage, but paymentConfirm does not exist'
          )
        }
      />
    );
  }

  if (!subscription) {
    return (
      <SomethingWentWrong
        error={
          new Error(
            'Tried to render ConnectOfferConfirmationPage, but subscription does not exist'
          )
        }
      />
    );
  }

  const nextBillingDate = new Date(subscription.createdAt ?? '');
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);

  return (
    <AppPage.Container>
      <AppPage.Content className="max-w-ll pt-24 lm:pt-64">
        <SubscriptionSummaryCard
          subscription={subscription}
          paymentConfirm={paymentConfirm}
          to={URLS.STORE_CONNECT_OFFER_DOWNLOAD_APPS}
        />

        <Feedback
          className="my-32"
          commentContext={commentContext}
          commentOnly
          title={t`We'd love to hear what made you start your free trial today`}
          feedbackTargetId="store-connect-offer-why-activate-free-trial-v1"
        />
      </AppPage.Content>
    </AppPage.Container>
  );
};
